import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowRight, Check, X, PaperPlaneTilt, Receipt, Storefront, ChartLineUp,
  QrCode, ChartBar, Envelope, Bell, UserPlus, Lock, ShieldCheck, Users, CurrencyDollar,
  Wallet, ChatCircleDots, Star, Gift, Crown, Lightning, Globe, Download, MagnifyingGlass,
  Megaphone, PencilSimple, Palette, CreditCard, FileText, Handshake, TrendUp
} from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

// Tri-state access: full ✓, partial ~, none ✗
const FEATURE_MATRIX = [
  {
    category: 'Discovery & Browsing',
    items: [
      { name: 'Explore marketplace', desc: 'Browse all public listings with filters', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Search by $username', desc: 'Find sellers by their handle', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Vendor Feed', desc: 'Personalized feed of preferred vendors', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Favorites', desc: 'Save items to revisit later', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Visit Storefronts', desc: 'Branded seller pages at /store/:username', buyer: 'full', seller: 'full', admin: 'full' },
    ]
  },
  {
    category: 'Payments & Transactions',
    items: [
      { name: 'Buy any listing type', desc: 'All 8 listing types — products, services, tips, bookings, etc.', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Stripe checkout', desc: 'Card, Apple Pay, Google Pay, Cash App', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'PayPal checkout', desc: 'Pay with PayPal balance or linked account', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Pay from Wallet', desc: 'Use Divispend balance', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Send Money (P2P)', desc: 'Transfer funds to any @username', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Request Money', desc: 'Create a payment request', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Transaction History', desc: 'Full ledger of every payment', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'PDF Receipts / Invoices', desc: 'Auto-generated, shareable', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Withdraw to bank', desc: 'Cash out earnings', buyer: 'full', seller: 'full', admin: 'full' },
    ]
  },
  {
    category: 'Selling & Listings',
    items: [
      { name: 'Create listings', desc: 'Any of 8 types: Product, Service, Subscription, Crowdfund, Offer, Tip Jar, Download, Booking', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'Rich text editor', desc: 'Styled product descriptions', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'Multi-image galleries', desc: 'Multiple images per listing (AWS S3)', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'DiviSpend revenue sharing', desc: 'Toggle investor revenue-share on any listing', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'Crowdfunding goals', desc: 'Progress bars, campaign tracking', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'Subscription tiers', desc: 'Recurring monthly billing + min commitment', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'Offer approval workflow', desc: 'Accept/reject "Make an Offer" proposals', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'Booking calendar', desc: 'Time-slot scheduling for services', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'Digital download delivery', desc: 'Instant file delivery post-purchase', buyer: 'none', seller: 'full', admin: 'full' },
    ]
  },
  {
    category: 'Storefront & Branding',
    items: [
      { name: 'Custom Storefront', desc: 'Branded page at /store/:username', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'Theme customization', desc: 'Colors, fonts, layout', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'Profile avatar & bio', desc: 'Personal brand basics', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Blue Certified badge', desc: 'Verified seller checkmark', buyer: 'none', seller: 'partial', admin: 'full', note: 'Pro tier' },
      { name: 'Custom pay link', desc: 'Shareable /pay/:username URL', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'Embeddable widget', desc: 'Add checkout to external sites', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'QR codes', desc: 'For profile, listings, and pay links', buyer: 'full', seller: 'full', admin: 'full' },
    ]
  },
  {
    category: 'Invest & Earn',
    items: [
      { name: 'DiviSpend investing', desc: 'Invest in any DiviSpend-enabled listing for a % of sales', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Investor Dashboard', desc: 'Track ROI, earnings, referrals', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Referral links', desc: '?ref= tracking with rewards', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Invite Friends', desc: 'Share via email, WhatsApp, SMS, QR', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Social marketing tools', desc: 'Pre-built share cards for Twitter/Facebook/LinkedIn', buyer: 'full', seller: 'full', admin: 'full' },
    ]
  },
  {
    category: 'Seller Analytics & CRM',
    items: [
      { name: 'Seller Analytics', desc: 'Revenue, trends, top listings', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'Advanced Analytics', desc: 'Cohorts, LTV, conversion', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'CRM dashboard', desc: 'Manage buyers, notes, tags', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'Direct messaging', desc: '1-on-1 chat with customers', buyer: 'partial', seller: 'full', admin: 'full', note: 'Buyers reply, sellers initiate' },
      { name: 'Broadcast emails', desc: 'Mass announcements to customers', buyer: 'none', seller: 'full', admin: 'full' },
      { name: 'Reviews & ratings', desc: 'See & respond to reviews', buyer: 'partial', seller: 'full', admin: 'full', note: 'Buyers submit, sellers respond' },
    ]
  },
  {
    category: 'Notifications',
    items: [
      { name: 'Email notifications', desc: 'Receipts, alerts, updates (via Resend)', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Push notifications', desc: 'Real-time alerts on web & PWA', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'In-app notification center', desc: 'All notifications in one place', buyer: 'full', seller: 'full', admin: 'full' },
    ]
  },
  {
    category: 'Security & Compliance',
    items: [
      { name: 'Two-Factor Authentication', desc: 'TOTP app-based 2FA', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Fraud detection', desc: 'Automated risk scoring', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Audit logs', desc: 'Full action history on your account', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'GDPR export/delete', desc: 'Data portability & erasure', buyer: 'full', seller: 'full', admin: 'full' },
      { name: 'Privacy controls', desc: 'Profile visibility settings', buyer: 'full', seller: 'full', admin: 'full' },
    ]
  },
  {
    category: 'Platform Admin',
    items: [
      { name: 'User management', desc: 'Ban, delete, adjust balances', buyer: 'none', seller: 'none', admin: 'full' },
      { name: 'Platform analytics', desc: 'Company-wide metrics', buyer: 'none', seller: 'none', admin: 'full' },
      { name: 'Fee configuration', desc: 'Set platform fee rates', buyer: 'none', seller: 'none', admin: 'full' },
      { name: 'Withdrawal approvals', desc: 'Approve/reject cashouts', buyer: 'none', seller: 'none', admin: 'full' },
      { name: 'Fraud review', desc: 'Review flagged transactions', buyer: 'none', seller: 'none', admin: 'full' },
      { name: 'Feedback inbox', desc: 'Review user feature requests/bugs', buyer: 'none', seller: 'none', admin: 'full' },
      { name: 'Domain management', desc: 'Custom domain configuration', buyer: 'none', seller: 'none', admin: 'full' },
    ]
  },
];

const AccessCell = ({ level, note }) => {
  if (level === 'full') {
    return (
      <div className="flex flex-col items-center gap-1" data-testid="access-full">
        <div className="w-7 h-7 rounded-full bg-[#22C55E]/15 border border-[#22C55E]/40 flex items-center justify-center">
          <Check className="w-4 h-4 text-[#22C55E]" weight="bold" />
        </div>
        {note && <span className="text-[10px] text-[#22C55E] font-medium">{note}</span>}
      </div>
    );
  }
  if (level === 'partial') {
    return (
      <div className="flex flex-col items-center gap-1" data-testid="access-partial">
        <div className="w-7 h-7 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/40 flex items-center justify-center">
          <span className="text-[#F59E0B] font-bold text-sm leading-none">~</span>
        </div>
        {note && <span className="text-[10px] text-[#F59E0B] font-medium">{note}</span>}
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-1" data-testid="access-none">
      <div className="w-7 h-7 rounded-full bg-[#333333]/50 border border-[#444444] flex items-center justify-center">
        <X className="w-4 h-4 text-[#555555]" weight="bold" />
      </div>
    </div>
  );
};

const RoleTab = ({ active, onClick, icon: Icon, label, color, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-sm transition-all font-semibold text-sm ${
      active
        ? 'bg-white text-[#111111]'
        : 'bg-[#1A1A1A] text-[#999999] hover:text-white hover:bg-[#222222]'
    }`}
  >
    <Icon className="w-4 h-4" weight="duotone" style={{ color: active ? color : undefined }} />
    {label}
  </button>
);

export default function FeaturesPage() {
  const { isAuthenticated } = useAuth();
  const [mobileRole, setMobileRole] = useState('buyer');

  return (
    <div className="min-h-screen bg-[#111111]">
      {/* Header */}
      <header className="bg-[#111111]/80 backdrop-blur-xl border-b border-[#222222] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="features-logo-link">
            <img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
          <nav className="flex items-center gap-3">
            {isAuthenticated ? (
              <Link to="/dashboard"><Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm" data-testid="features-dashboard-button">Dashboard <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm" data-testid="features-login-button">Sign in</Button></Link>
                <Link to="/register"><Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm" data-testid="features-register-button">Get Started</Button></Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 sm:py-24 border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#22C55E] mb-3">Every feature, every role</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
            What you get on Divispend
          </h1>
          <p className="text-[#999999] max-w-2xl mx-auto text-base">
            Whether you're here to buy, sell, invest, or run the show — here's exactly what's unlocked for you.
          </p>
        </div>
      </section>

      {/* Role Legend — Desktop */}
      <section className="hidden lg:block pt-12 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-4 items-end">
            <div></div>
            <div className="bg-[#1A1A1A] border border-[#222222] rounded-sm p-5 text-center">
              <Users className="w-7 h-7 mx-auto mb-2 text-[#22C55E]" weight="duotone" />
              <h3 className="text-white font-bold text-sm mb-1" style={{ fontFamily: 'Cabinet Grotesk' }}>Buyer</h3>
              <p className="text-[#666666] text-[11px] leading-tight">Browse, pay, invest, engage</p>
            </div>
            <div className="bg-[#1A1A1A] border border-[#22C55E]/30 rounded-sm p-5 text-center relative">
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#22C55E] text-[#111111] text-[10px] font-bold px-2 py-0.5 rounded-sm">MOST POPULAR</div>
              <Storefront className="w-7 h-7 mx-auto mb-2 text-[#22C55E]" weight="duotone" />
              <h3 className="text-white font-bold text-sm mb-1" style={{ fontFamily: 'Cabinet Grotesk' }}>Seller</h3>
              <p className="text-[#666666] text-[11px] leading-tight">Everything a buyer gets + 30+ seller tools</p>
            </div>
            <div className="bg-[#1A1A1A] border border-[#222222] rounded-sm p-5 text-center">
              <Crown className="w-7 h-7 mx-auto mb-2 text-[#22C55E]" weight="duotone" />
              <h3 className="text-white font-bold text-sm mb-1" style={{ fontFamily: 'Cabinet Grotesk' }}>Admin</h3>
              <p className="text-[#666666] text-[11px] leading-tight">Platform operator</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Role Tabs */}
      <section className="lg:hidden py-6 sticky top-[73px] bg-[#111111] z-40 border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex gap-2">
            <RoleTab active={mobileRole === 'buyer'} onClick={() => setMobileRole('buyer')} icon={Users} label="Buyer" color="#22C55E" testId="mobile-tab-buyer" />
            <RoleTab active={mobileRole === 'seller'} onClick={() => setMobileRole('seller')} icon={Storefront} label="Seller" color="#22C55E" testId="mobile-tab-seller" />
            <RoleTab active={mobileRole === 'admin'} onClick={() => setMobileRole('admin')} icon={Crown} label="Admin" color="#22C55E" testId="mobile-tab-admin" />
          </div>
        </div>
      </section>

      {/* Feature Matrix */}
      <section className="py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-10">
          {FEATURE_MATRIX.map((group) => (
            <div key={group.category} data-testid={`feature-group-${group.category.toLowerCase().replace(/\s+/g, '-')}`}>
              <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#22C55E] mb-4 border-b border-[#222222] pb-2">
                {group.category}
              </h2>

              {/* Desktop table */}
              <div className="hidden lg:block">
                <div className="divide-y divide-[#1A1A1A]">
                  {group.items.map((item) => (
                    <div key={item.name} className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-4 py-4 items-center hover:bg-[#0A0A0A] transition-colors px-3 -mx-3 rounded-sm">
                      <div>
                        <div className="text-white font-semibold text-sm mb-0.5" style={{ fontFamily: 'Cabinet Grotesk' }}>{item.name}</div>
                        <div className="text-[#999999] text-xs">{item.desc}</div>
                      </div>
                      <div className="flex justify-center"><AccessCell level={item.buyer} note={item.buyer === 'partial' ? item.note : null} /></div>
                      <div className="flex justify-center"><AccessCell level={item.seller} note={item.seller === 'partial' ? item.note : null} /></div>
                      <div className="flex justify-center"><AccessCell level={item.admin} /></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile list */}
              <div className="lg:hidden space-y-3">
                {group.items.map((item) => {
                  const level = item[mobileRole];
                  const note = level === 'partial' ? item.note : null;
                  return (
                    <div key={item.name} className="bg-[#1A1A1A] border border-[#222222] rounded-sm p-4 flex items-start gap-3">
                      <AccessCell level={level} note={note} />
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-semibold text-sm mb-0.5" style={{ fontFamily: 'Cabinet Grotesk' }}>{item.name}</div>
                        <div className="text-[#999999] text-xs">{item.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Legend */}
          <div className="bg-[#0A0A0A] border border-[#222222] rounded-sm p-5 flex flex-wrap gap-6 justify-center" data-testid="features-legend">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[#22C55E]/15 border border-[#22C55E]/40 flex items-center justify-center">
                <Check className="w-3.5 h-3.5 text-[#22C55E]" weight="bold" />
              </div>
              <span className="text-[#999999] text-xs">Full access</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/40 flex items-center justify-center">
                <span className="text-[#F59E0B] font-bold text-xs leading-none">~</span>
              </div>
              <span className="text-[#999999] text-xs">Partial / contextual</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[#333333]/50 border border-[#444444] flex items-center justify-center">
                <X className="w-3.5 h-3.5 text-[#555555]" weight="bold" />
              </div>
              <span className="text-[#999999] text-xs">Not available</span>
            </div>
          </div>
        </div>
      </section>

      {/* How to become a seller */}
      <section className="py-16 bg-[#0A0A0A] border-t border-[#222222]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-[#22C55E]/10 to-transparent border border-[#22C55E]/20 rounded-sm p-8 sm:p-10 text-center">
            <Lightning className="w-10 h-10 text-[#22C55E] mx-auto mb-4" weight="duotone" />
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Cabinet Grotesk' }}>Every buyer can become a seller — instantly</h2>
            <p className="text-[#999999] max-w-xl mx-auto text-sm mb-6">
              There's no separate signup, application, or approval. Create your first listing and you're a seller. Stay a buyer if that's all you want. The choice is always yours.
            </p>
            <Link to={isAuthenticated ? "/products/new" : "/register"}>
              <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-11 px-6 font-semibold" data-testid="features-become-seller-button">
                {isAuthenticated ? "Create your first listing" : "Create free account"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>Ready to get started?</h2>
          <p className="text-[#999999] mb-8 max-w-md mx-auto">Join creators and entrepreneurs building their businesses on Divispend.</p>
          <Link to={isAuthenticated ? "/dashboard" : "/register"}>
            <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-12 px-8 text-base font-semibold" data-testid="features-cta-button">
              {isAuthenticated ? "Go to Dashboard" : "Create free account"} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#222222] py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={LOGO_URL} alt="Divispend" className="h-8 w-auto" />
          <div className="flex items-center gap-6">
            <Link to="/features" className="text-sm text-[#999999] hover:text-white transition-colors" data-testid="footer-features-link">Features</Link>
            <Link to="/trust" className="text-sm text-[#999999] hover:text-white transition-colors" data-testid="footer-trust-link">Trust &amp; Security</Link>
            <Link to="/privacy_policy" className="text-sm text-[#999999] hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <p className="text-sm text-[#999999]">&copy; 2026 Divispend. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
